import { Box, Flex, Text } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { assessmentBadges } from 'src/components/Review/Badges';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import ReviewTableScore from 'src/components/ReviewTableScore';
import AssessmentOptIns from 'src/components/Assessment/AssessmentOptIns';
import { FileListDisplay } from 'src/components/Submission/GenericFieldComponents/FileList/FileListDisplay';
import { IAdjudicationFormSubmission, IInvitedAdjudicator } from '@publishing-engine/domain/models';
import { faClockFour } from '@fortawesome/pro-regular-svg-icons';
import { allKeysHaveValues } from 'src/pages/MyTask/Assessments/hooks';

export type IProps = {
	onClose: () => void;
	adjudicationFeedback: IAdjudicationFormSubmission;
	configs: any;
	invitedAdjudicator: IInvitedAdjudicator;
	roundIndex: number;
}


const AdjudicationDetailsModal = ({ onClose, adjudicationFeedback, configs, invitedAdjudicator, roundIndex }: IProps) => {
	const { submissionScores, feedbackForAuthors, authorsAttachments, editorAttachments, feedbackForEditors, webOfScienceOptIn, reviewerAnonymity, peerReviewTransparency } = adjudicationFeedback
	const hasOptIns = webOfScienceOptIn || reviewerAnonymity || peerReviewTransparency
	const isSubmissionScoresData = allKeysHaveValues(submissionScores)
	const additionalReference = adjudicationFeedback && 'missingReference' in adjudicationFeedback ? adjudicationFeedback?.missingReference : undefined

	return (
		<Overlay height="100%">
			<Flex
				width="100vw"
				flexDirection="column"
				backgroundColor="surfaceSecondaryFill"
			>
				<Layout width="48rem">
					<Flex flexDirection="column" gap={6} padding={8}>
						<ButtonText
							leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
							maxWidth="fit-content"
							onClick={onClose}
							variant="secondary"
						>
							Back
						</ButtonText>
						<Flex flexDirection="column" gap={2}>
							<Flex justifyContent="space-between" alignItems="center">
								<Flex gap={2} flexDirection="column">
									<Flex gap={1} alignItems="center">
										<FontAwesomeIcon icon={faClockFour} />
										<Text textStyle="textBody">From Round {roundIndex}</Text>
									</Flex>
									<Text textStyle="headingTitle">Assess this Adjudication</Text>
									<Text>{invitedAdjudicator?.firstName}{' '}
										{invitedAdjudicator?.lastName}</Text>
								</Flex>
							</Flex>
						</Flex>

						<Flex
							flexDirection="column"
							gap={6}
							backgroundColor="surfacePrimaryFill"
							padding={8}
						>
							{adjudicationFeedback?.recommendation && (
								<Flex flexDirection="column" gap={6}>
									<Text textStyle="headingGroup">Recommendation</Text>
									<Flex>
										{assessmentBadges[adjudicationFeedback.recommendation]}
									</Flex>
								</Flex>
							)}
							{submissionScores && isSubmissionScoresData && configs && (
								<Box flexDirection="column">
									<Text textStyle="headingGroup">Submission Scores</Text>
									<Box marginX={2} marginTop={4}>
										<ReviewTableScore
											submissionScores={submissionScores}
											reviewFieldConfig={configs}
										/>
									</Box>
								</Box>
							)}
							{(feedbackForEditors || (editorAttachments &&
								editorAttachments?.length > 0)) &&
								<>
									<Text textStyle="headingGroup">Communications For Editors</Text>
									{feedbackForEditors &&
										<><Text textStyle="headingItem">Feedback for Editors</Text>
											<Text textStyle="textBody">
												{feedbackForEditors}
											</Text>
										</>}
									{editorAttachments &&
										editorAttachments?.length > 0 && (
											<>
												<Text textStyle="headingItem">Attached Files</Text>
												<Flex flexDirection="column" gap={4}>
													<FileListDisplay
														files={editorAttachments}
														buttonIcon={true}
													/>
												</Flex>
											</>
										)}

								</>}

							{(feedbackForAuthors ||
								(authorsAttachments &&
									authorsAttachments?.length > 0)) && (
									<Text textStyle="headingGroup">Communications For Authors</Text>
								)}

							{feedbackForAuthors && (
								<>
									<Text textStyle="headingItem">Feedback for Authors</Text>
									<Text textStyle="textBody">
										{feedbackForAuthors}
									</Text>
								</>
							)}

							{authorsAttachments &&
								authorsAttachments?.length > 0 && (
									<Text textStyle="headingItem">Attached Files</Text>
								)}
							<Flex flexDirection="column" gap={4}>
								{authorsAttachments &&
									authorsAttachments?.length > 0 && (
										<FileListDisplay
											files={authorsAttachments}
											buttonIcon={true}
										/>
									)}
							</Flex>
							{additionalReference?.exists && (
								<Flex flexDirection="column" gap={2}>
									<Text textStyle="headingItem">
										Additional References
									</Text>
									<Text textStyle="textBody">{additionalReference?.value}</Text>
								</Flex>
							)}
							{adjudicationFeedback && hasOptIns && <>
								<Text textStyle="headingGroup">Opt Ins</Text>
								<AssessmentOptIns
									earlyAssessment={adjudicationFeedback}
									config={{
										PeerReviewTransparency: configs.peerReviewTransparency,
										ReviewerAnonymity: configs.reviewerAnonymity,
										WebOfScienceOptIn: configs.webOfScienceOptIn
									}}
								/>
							</>}
						</Flex>
					</Flex>
				</Layout>
			</Flex>
		</Overlay>
	)
}

export default AdjudicationDetailsModal
